import {priority} from '@priority-software/priority-proto-api/lib';
import {Flex, Input, PriorityIcon, Text} from '@priority-software/priority-style-react';
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';
import classNames from 'classnames';

import {keys} from '../../../constants/KeyCodes';
import {CellSearchButton, OpenSearchDropdownAPI} from '../../Form/components/CellSearchButton';
import {APIActionStatus, Column, Field, apiLocker, formsStore} from '../../../stores/Form';
import {PREVENT_SEARCH_FIEDS, RELEVANT_FIELDS_NAMES} from '../consts';
import styles from './styles.module.scss';
import {getTopDialogZindex} from '../../../_shared/GWTBridge/invoke/priModalDlg';
import {StatusIcon} from '../../Form/components/StatusIcon';
import {isRTL} from '../../../Localization';

interface Props {
    field: Field;
    column: Column;
    isDisabled: boolean;
    focus?: boolean;
    isFocused: boolean;
    focusNextInput?: (name?: string, isBackword?: boolean) => void;
}

const FormControl = forwardRef(
    ({field, column, isDisabled, isFocused, focusNextInput}: Props, ref) => {
        const [value, setValue] = useState(field.origValue as string);
        const inputRef = useRef<HTMLInputElement>();
        const contentRef = useRef<HTMLDivElement>();
        const [isDirty, setIsDirty] = useState(false);

        let isDropOpen = false;
        const isSearch =
            column.Zoom === priority.netitems.form.ZoomType.SelectZoom &&
            !PREVENT_SEARCH_FIEDS.includes(column.Name);

        const fieldSearchRef = useRef<OpenSearchDropdownAPI>();

        useEffect(() => {
            setValue(field.origValue as string);
        }, [field.origValue]);

        useEffect(() => {
            isFocused && focus();
        }, [isFocused]);

        useImperativeHandle(ref, () => {
            return {
                focus,
                isDisabled,
                name: column.Name
            };
        });

        const onUpdateField = async (isSearchSelect?: boolean, name?: string) => {
            if (isDirty || isSearchSelect) {
                const actionStatus = await formsStore.currentScreen.fieldUpdate(field);
                const isSuccess = APIActionStatus.passed === actionStatus;
                if (isSuccess) {
                    setIsDirty(false);
                    focusNextInput(name);
                } else {
                    focus();
                }
            }
        };

        const focus = () => {
            inputRef?.current?.focus();
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
            e.persist();
            const {key} = e;
            switch (key) {
                case keys.SPACE:
                case keys.F6:
                    if (fieldSearchRef.current) {
                        if (!fieldSearchRef.current?.isOpen && (value === '' || keys.F6 === key)) {
                            e.stopPropagation();
                            e.preventDefault();
                            fieldSearchRef.current.openSearchDropdown();
                            isDropOpen = true;
                        }
                    }
                    break;
                case keys.TAB:
                case keys.ENTER:
                    e.preventDefault();
                    e.stopPropagation();
                    const isBack = keys.TAB === key && e.shiftKey;
                    focusNextInput(null, isBack);
                    break;
                case keys.ARROW_LEFT:
                case keys.ARROW_RIGHT:
                    e.preventDefault();
                    e.stopPropagation();
                    const isBackword = isRTL() ? keys.ARROW_RIGHT === key : keys.ARROW_LEFT === key;
                    focusNextInput(null, isBackword);
                    break;
                default:
                    break;
            }
        };

        const handleBlur = async e => {
            if (apiLocker.isLock(formsStore.currentForm.session)) {
                e.preventDefault();
            } else {
                if (isSearch && isDropOpen) return;
                e.persist();
                let name;
                if (RELEVANT_FIELDS_NAMES.includes(e?.relatedTarget?.name)) {
                    name = e.relatedTarget.name;
                }
                onUpdateField(false, name);
            }
        };

        const handleChange = val => {
            field.setCurrentValue(val);
            setValue(val);
            !isDirty && setIsDirty(true);
        };

        const handleSelect = async val => {
            field.setCurrentValue(val);
            onUpdateField(true);
        };

        const onmousedown = e => {
            if (!isDropOpen) isDropOpen = true;
        };

        const getCellWrap = useCallback(() => contentRef.current, []);

        const inputClasses = classNames(styles.controlInput, {
            [styles.disabledControlInput]: isDisabled,
            [styles.searchInput]: isSearch
        });

        return (
            <Flex
                componentClasses={styles.formControl}
                justifyContent="flex-start"
                alignItems="flex-start"
                direction="column"
            >
                <Flex
                    componentClasses={styles.formControlLabel}
                    justifyContent="flex-start"
                    alignItems="flex-start"
                >
                    <Text skin="subtitle-1" textColor="primary-700">
                        {column.Title}
                    </Text>
                    {column.Mandatory && (
                        <Text
                            componentClasses={styles.formMendatoryIcon}
                            tag="span"
                            textColor="red"
                            skin="subtitle-1"
                        >
                            *
                        </Text>
                    )}
                </Flex>
                <div ref={contentRef} className={styles.inputWrapper}>
                    <Input
                        maxLength={column.Width as number}
                        autocomplete="off"
                        value={value}
                        size="large"
                        direction="auto"
                        name={column.Name}
                        onKeyDown={handleKeyDown}
                        componentClasses={inputClasses}
                        disabled={isDisabled}
                        onBlur={handleBlur}
                        inputRef={inputRef}
                        onChange={e => handleChange(e.target.value)}
                        prefixIcon={
                            column.IsStatus &&
                            field.origValue && (
                                <StatusIcon statuses={column.parentForm.statuses} status={value} />
                            )
                        }
                    />
                    {isSearch && (
                        <div onMouseDown={onmousedown} className={styles.searchIconWrapper}>
                            <CellSearchButton
                                ref={fieldSearchRef}
                                column={column}
                                getCellWrap={getCellWrap}
                                searchValue={value}
                                onSelect={handleSelect}
                                zindex={getTopDialogZindex()}
                                icon={PriorityIcon.arrowBottom}
                                isAutoComplete={false}
                                dropdownInputType="external"
                                onClose={() => {
                                    focus();
                                    isDropOpen = false;
                                }}
                            />
                        </div>
                    )}
                </div>
            </Flex>
        );
    }
);

export default FormControl;
